/*       */

/**
 * Form field metadata providing user-friendly names, descriptions, icons and categories
 * for the form designer component picker.
 */

export const formFieldCategories = [
    { id: 'input', name: 'Input Fields', order: 1 },
    { id: 'selection', name: 'Selection & Pickers', order: 2 },
    { id: 'datetime', name: 'Date & Time', order: 3 },
    { id: 'layout', name: 'Layout', order: 4 },
    { id: 'geolocation', name: 'Geolocation', order: 5 },
    { id: 'buttons', name: 'Buttons & Actions', order: 6 },
    { id: 'media', name: 'Media & Files', order: 7 },
    { id: 'advanced', name: 'Advanced', order: 8 },
];

export const formFieldMetadata = {
    // Input Fields
    text: {
        name: 'Text Input',
        description: 'Single-line text field',
        category: 'input',
        icon: 'form-textbox',
    },
    textarea: {
        name: 'Text Area',
        description: 'Multi-line text field for longer content',
        category: 'input',
        icon: 'form-textarea',
    },
    textEditor: {
        name: 'Rich Text Editor',
        description: 'Text editor with formatting options',
        category: 'input',
        icon: 'format-text',
    },
    number: {
        name: 'Number Input',
        description: 'Numeric value input with validation',
        category: 'input',
        icon: 'numeric',
    },
    duration: {
        name: 'Duration',
        description: 'Time duration picker (hours, minutes, seconds)',
        category: 'input',
        icon: 'timer-outline',
    },
    boolean: {
        name: 'Checkbox',
        description: 'Toggle for yes/no values',
        category: 'input',
        icon: 'checkbox-marked',
    },
    json: {
        name: 'JSON Editor',
        description: 'Structured JSON data input',
        category: 'input',
        icon: 'code-json',
    },

    // Selection & Pickers
    typeahead: {
        name: 'Dropdown',
        description: 'Searchable dropdown selection',
        category: 'selection',
        icon: 'menu-down-outline',
    },
    entityTypeahead: {
        name: 'Entity Picker',
        description: 'Select entities from the system',
        category: 'selection',
        icon: 'entities',
        iconType: 'af',
    },
    userTypeahead: {
        name: 'User Picker',
        description: 'Select users from the system',
        category: 'selection',
        icon: 'people',
        iconType: 'af',
    },
    classificationTypeahead: {
        name: 'Classification Picker',
        description: 'Select classifications',
        category: 'selection',
        icon: 'Attribute-class',
        iconType: 'af',
    },
    graphicTypeahead: {
        name: 'Graphic Picker',
        description: 'Select graphics',
        category: 'selection',
        icon: 'image-outline',
    },
    taskTypeahead: {
        name: 'Task Picker',
        description: 'Select tasks',
        category: 'selection',
        icon: 'checkbox-marked-circle-outline',
    },
    processTypeahead: {
        name: 'Process Picker',
        description: 'Select processes',
        category: 'selection',
        icon: 'processes',
        iconType: 'af',
    },
    teamTypeahead: {
        name: 'Team Picker',
        description: 'Select teams',
        category: 'selection',
        icon: 'account-group-outline',
    },
    workspaceTypeahead: {
        name: 'Workspace Picker',
        description: 'Select workspaces',
        category: 'selection',
        icon: 'briefcase-outline',
    },
    relationsTypeahead: {
        name: 'Relations Picker',
        description: 'Select entity relations',
        category: 'selection',
        icon: 'relationships',
        iconType: 'af',
    },
    relationDefinitionTypeahead: {
        name: 'Relation Type Picker',
        description: 'Select relation definitions',
        category: 'selection',
        icon: 'relationships',
        iconType: 'af',
    },
    colorPicker: {
        name: 'Color Picker',
        description: 'Choose colors visually',
        category: 'selection',
        icon: 'palette',
    },
    iconSelect: {
        name: 'Icon Picker',
        description: 'Select from available icons',
        category: 'selection',
        icon: 'star',
    },
    slider: {
        name: 'Slider',
        description: 'Numeric value slider control',
        category: 'selection',
        icon: 'tune-vertical',
    },
    classification: {
        name: 'Classification',
        description: 'Display classification badges',
        category: 'selection',
        icon: 'tag-outline',
    },
    radioGroup: {
        name: 'Radio Group',
        description: 'Group of radio buttons for single selection',
        category: 'selection',
        icon: 'radiobox-marked',
    },

    // Date & Time
    date: {
        name: 'Date Picker',
        description: 'Select a date',
        category: 'datetime',
        icon: 'calendar',
    },
    time: {
        name: 'Time Picker',
        description: 'Select a time',
        category: 'datetime',
        icon: 'clock-outline',
    },
    dateTime: {
        name: 'Date & Time Picker',
        description: 'Select date and time together',
        category: 'datetime',
        icon: 'calendar-clock',
    },
    dateTimeRange: {
        name: 'Date Range Picker',
        description: 'Select start and end dates',
        category: 'datetime',
        icon: 'calendar-range',
    },

    // Layout
    group: {
        name: 'Group',
        description: 'Container for grouping fields',
        category: 'layout',
        icon: 'folder-outline',
    },
    groupRepeat: {
        name: 'Repeating Group',
        description: 'Repeatable group of fields',
        category: 'layout',
        icon: 'repeat',
    },
    panel: {
        name: 'Panel',
        description: 'Visual container with styling',
        category: 'layout',
        icon: 'card-outline',
    },
    header: {
        name: 'Header',
        description: 'Section header text',
        category: 'layout',
        icon: 'format-header-1',
    },
    displayText: {
        name: 'Display Text',
        description: 'Read-only formatted text',
        category: 'layout',
        icon: 'text',
    },

    // Geolocation
    geotagPosition: {
        name: 'GPS Coordinates',
        description: 'Capture latitude/longitude',
        category: 'geolocation',
        icon: 'crosshairs-gps',
    },
    geotagButton: {
        name: 'Location Button',
        description: 'Button to capture current location',
        category: 'geolocation',
        icon: 'map-marker-plus',
    },
    geotagLocation: {
        name: 'Location Display',
        description: 'Show location on map',
        category: 'geolocation',
        icon: 'map-marker-radius',
    },
    geotagAddress: {
        name: 'Address',
        description: 'Address input with geocoding',
        category: 'geolocation',
        icon: 'home-map-marker',
    },

    // Buttons & Actions
    button: {
        name: 'Button',
        description: 'Clickable action button',
        category: 'buttons',
        icon: 'rectangle',
    },
    openAppButton: {
        name: 'Open App Button',
        description: 'Button to open another application',
        category: 'buttons',
        icon: 'open-in-new',
    },
    printButton: {
        name: 'Print Button',
        description: 'Button to trigger printing',
        category: 'buttons',
        icon: 'printer',
    },
    outcome: {
        name: 'Outcome',
        description: 'Process outcome selection',
        category: 'buttons',
        icon: 'flag-checkered',
    },

    // Media & Files
    image: {
        name: 'Image',
        description: 'Display or upload images',
        category: 'media',
        icon: 'image',
    },
    icon: {
        name: 'Icon',
        description: 'Display an icon',
        category: 'media',
        icon: 'star-outline',
    },
    dropzone: {
        name: 'File Upload',
        description: 'Drag-and-drop file upload area',
        category: 'media',
        icon: 'upload',
    },
    file: {
        name: 'File Attachment',
        description: 'File attachment field',
        category: 'media',
        icon: 'paperclip',
    },
    media: {
        name: 'Media',
        description: 'Entity media file attachments with staged upload',
        category: 'media',
        icon: 'file-multiple',
    },
    hyperlink: {
        name: 'Hyperlink',
        description: 'Clickable URL link',
        category: 'media',
        icon: 'link',
    },
    chip: {
        name: 'Chip/Tag',
        description: 'Display tags or chips',
        category: 'media',
        icon: 'label-outline',
    },

    // Advanced
    iotPanel: {
        name: 'IoT Panel',
        description: 'Connect to IoT devices',
        category: 'advanced',
        icon: 'developer-board',
    },
    entityList: {
        name: 'Entity List',
        description: 'Display list of related entities',
        category: 'advanced',
        icon: 'format-list-bulleted',
    },
};

/**
 * Get metadata for a field type with fallback for unknown types.
 * @param {string} type - The field type identifier
 * @returns {Object} Metadata object with name, description, icon and category
 */
export const getFieldMetadata = (type) => {
    return formFieldMetadata[type] || {
        name: type,
        description: '',
        category: 'advanced',
        icon: 'help-circle-outline',
        iconType: 'mdi',
    };
};

/**
 * Get the display name for a category ID.
 * @param {string} categoryId - The category identifier
 * @returns {string} The display name for the category
 */
export const getCategoryName = (categoryId) => {
    const category = formFieldCategories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
};
